/**
 * Single source of truth for severity ordering, ranking, and visual
 * encoding (badge classes + chart fills) across the SPA.
 *
 * Extracted in Fraud Hub PR 2 from inline constants previously
 * duplicated in FraudCenter.tsx (SEVERITY_BADGE) and
 * FraudDashboard.tsx (SEVERITY_STYLE). Keep new severity-related
 * constants here so visuals stay consistent across pages.
 */

import type { Severity } from '@/types/alerts';

/** Display order — most-urgent first. Used as the canonical sort. */
export const SEVERITY_ORDER: Severity[] = [
  'Critical',
  'High',
  'Medium',
  'Low',
  'Watch',
  'Normal',
];

/**
 * Numeric rank: Critical=1 (most urgent) … Normal=6 (least). Use this
 * inside comparators and SQL-style ORDER BY CASE bridges.
 */
export const SEVERITY_RANK: Record<Severity, number> = {
  Critical: 1,
  High: 2,
  Medium: 3,
  Low: 4,
  Watch: 5,
  Normal: 6,
};

/**
 * Muted badge variant used in the dense Fraud Center / Fraud Dashboard
 * tables — readable at small sizes without overwhelming the row. Copied
 * verbatim from the previous inline `SEVERITY_BADGE` in FraudCenter.tsx
 * to preserve pixel-identical rendering.
 */
export const SEVERITY_BADGE_CLASS: Record<Severity, string> = {
  Critical: 'bg-red-100 text-red-800 border-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  Low: 'bg-blue-100 text-blue-800 border-blue-200',
  Watch: 'bg-slate-100 text-slate-700 border-slate-200',
  Normal: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

/**
 * Saturated badge variant used in the Fraud Hub Critical Alerts Board
 * (and any future "this is the urgency tier — read it loud" surface).
 * Higher contrast — solid colour fill, white text — so the urgency
 * carries from across the room.
 */
export const SEVERITY_BADGE_LARGE_CLASS: Record<Severity, string> = {
  Critical: 'bg-red-600 text-white border-red-700',
  High: 'bg-orange-600 text-white border-orange-700',
  Medium: 'bg-amber-500 text-white border-amber-600',
  Low: 'bg-blue-600 text-white border-blue-700',
  Watch: 'bg-slate-500 text-white border-slate-600',
  Normal: 'bg-emerald-600 text-white border-emerald-700',
};

/**
 * HSL fill values for chart series (Recharts <Bar fill={…} /> etc).
 * Copied verbatim from the previous inline `SEVERITY_STYLE.<sev>.fill`
 * map in FraudDashboard.tsx so chart colours stay identical.
 */
export const SEVERITY_FILL: Record<Severity, string> = {
  Critical: 'hsl(0 84% 60%)',
  High: 'hsl(24 95% 53%)',
  Medium: 'hsl(48 96% 53%)',
  Low: 'hsl(199 89% 48%)',
  Watch: 'hsl(215 20% 65%)',
  Normal: 'hsl(142 71% 45%)',
};

/**
 * Comparator that sorts severities most-urgent first. Use with
 * `[…].sort(compareBySeverity)` or `Array.prototype.sort` callbacks.
 */
export function compareBySeverity(a: Severity, b: Severity): number {
  return SEVERITY_RANK[a] - SEVERITY_RANK[b];
}

/**
 * Narrow an arbitrary wire string to a known `Severity`, or `null`.
 * Wire payloads type severity as a plain string (rule authors can emit
 * anything), so any surface that indexes one of the palettes above must
 * go through this guard rather than casting.
 */
export function asSeverity(value: string | null | undefined): Severity | null {
  return value != null && value in SEVERITY_RANK ? (value as Severity) : null;
}

/**
 * Same palette as `SEVERITY_FILL`, rewritten to the legacy comma form
 * (`hsl(0, 84%, 60%)`) for MapLibre paint properties.
 *
 * The chart palette uses the CSS Color 4 space-separated syntax, which
 * browsers accept but MapLibre's own colour parser is not guaranteed to.
 * Deriving from `SEVERITY_FILL` instead of hard-coding a second set of
 * values keeps the map blips and the charts on ONE palette.
 */
export function severityMapColor(severity: Severity): string {
  const css = SEVERITY_FILL[severity];
  const inner = /^hsl\(\s*([^)]+?)\s*\)$/.exec(css)?.[1];
  if (!inner || inner.includes(',')) return css;
  const parts = inner.split(/[\s/]+/).filter(Boolean);
  return parts.length >= 3 ? `hsl(${parts[0]}, ${parts[1]}, ${parts[2]})` : css;
}
